import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import lodash from 'lodash';
import { NOTIFICATION, REWARD_DROP } from '@/constants';
import { displayPrizeType } from '@/helpers';
import { PrizeDTO, SelectionOptions, TableHeaderColumns, RewardItemDisplayDTO, RewardItemRequest, ExchangeRatio } from '@/models';
import { createRewardItems, fetchOperationSettings, fetchPrizes } from '@/services';
import { Debounce } from '@/decorators';

import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';

@Component({
  components: {
    FilterDropdown,
    Pagination,
    SearchInput
  }
})
export default class AddRewardItemDialog extends Vue {
  @Prop() periodName: string;
  @Prop() rewardItems: RewardItemDisplayDTO[];

  $refs!: {
    formRef: HTMLFormElement;
  };

  private readonly headerTable: TableHeaderColumns = [
    {
      value: 'name',
      text: 'Name'
    },
    {
      value: 'image',
      text: 'Image'
    },
    {
      value: 'type',
      text: 'Type'
    },
    {
      value: 'estimatedPrice',
      text: 'Estimated Price (VND)'
    },
    {
      value: 'cost',
      text: 'Cost (Pollens)'
    },
    {
      value: 'quantity',
      text: 'Quantity'
    },
    {
      value: 'maxRedeemPerUser',
      text: 'Max per user'
    }
  ];
  private selectedRows: RewardItemDisplayDTO[] = [];

  private prizes: PrizeDTO[] = [];
  private prizeTypeOptions: SelectionOptions = [];
  private displayDataPrizes: RewardItemDisplayDTO[] = [];
  private searchPrize = '';
  private pageCount = 0;
  private page = 1;
  private itemsPerPage = 10;
  private selectedPrizeTypes = [] as string[];
  private rules = {
    required: (value: number) => !!value || 'Required',
    cost: (value: number) => value > 0 || 'Cost must be greater than zero',
    quantity: (value: number) => value >= 0 || 'Quantity must not be negative',
    maxRedeemPerUser: (value: number | string | null) =>
      this.normalizeMaxRedeemPerUser(value) !== -1 || 'Max per user must be greater than zero'
  };
  private rewardPeriodId = -1;
  private loading = false;
  private imageUrl = process.env.VUE_APP_HIVE_HOME_URL + '/images/';
  private dropMappingTable = REWARD_DROP;
  get addButtonText(): string {
    return this.selectedRows.length > 0 ? `Add ${this.selectedRows.length}` : 'Add';
  }

  get displayPrizes(): RewardItemDisplayDTO[] {
    return this.buildDisplayPrizes();
  }

  get isAddButtonDisabled(): boolean {
    return this.selectedRows.length === 0 || !this.isValidForAddRewardItem();
  }

  @Watch('rewardItems')
  onRewardItemsChanged(): void {
    this.setDisplayDataPrizes();
  }

  async created(): Promise<void> {
    this.loading = true;
    this.rewardPeriodId = parseInt(this.$route.params.id);
    await this.setInitialPrizes();
    this.setDisplayDataPrizes();
    this.loading = false;
  }

  setInitialPrizeTypeOptions(): void {
    lodash.uniqBy(this.displayDataPrizes, 'type').forEach(item => {
      this.prizeTypeOptions.push({ text: this.getPrizeType(item.type), value: item.type });
    });
  }

  async setInitialPrizes(): Promise<void> {
    const { data } = await fetchPrizes();
    this.prizes = data;
  }

  setDisplayDataPrizes(): void {
    this.displayDataPrizes = [];
    this.prizes.forEach(prize => this.displayDataPrizes.push(this.buildEmptyRewardItem(prize)));
    this.setInitialPrizeTypeOptions();
  }

  buildDisplayPrizes(): RewardItemDisplayDTO[] {
    let displayPrizes = this.displayDataPrizes;
    displayPrizes = this.filterPrizesByName(displayPrizes);
    displayPrizes = this.filterPrizesByType(displayPrizes);
    return displayPrizes;
  }

  filterPrizesByType(displayPrizes: RewardItemDisplayDTO[]): RewardItemDisplayDTO[] {
    return this.selectedPrizeTypes.length > 0
      ? displayPrizes.filter(displayPrize => this.selectedPrizeTypes.includes(displayPrize.type))
      : displayPrizes;
  }

  filterPrizesByName(displayPrizes: RewardItemDisplayDTO[]): RewardItemDisplayDTO[] {
    return !lodash.isEmpty(this.searchPrize)
      ? displayPrizes.filter(displayPrize => displayPrize.name.toLowerCase().includes(this.searchPrize))
      : displayPrizes;
  }

  onSearch(searchText: string): void {
    this.searchPrize = searchText;
    this.selectedRows = [];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  onPrizeTypeChange(selectedPrizeTypes: string[]): void {
    this.selectedPrizeTypes = selectedPrizeTypes;
    this.selectedRows = [];
  }

  isValidForAddRewardItem(): boolean {
    return !this.selectedRows.some(
      row => row.cost <= 0 || row.quantity < 0 || this.normalizeMaxRedeemPerUser(row.maxRedeemPerUser) === -1
    );
  }

  @Debounce(50)
  async onConfirmChange(): Promise<void> {
    if (this.selectedRows.length > 0) {
      const selectedRewardItems: RewardItemRequest[] = [];
      this.selectedRows.forEach(row => {
        selectedRewardItems.push({
          id: row.id,
          prizeId: row.prizeId,
          cost: row.cost,
          quantity: Number(row.quantity),
          maxRedeemPerUser: this.toRequestMaxRedeemPerUser(row.maxRedeemPerUser),
          estimatedPrice: row.estimatedPrice
        });
      });
      const addRewardItems = await createRewardItems(selectedRewardItems, this.rewardPeriodId);
      if (addRewardItems.data) {
        const data = {
          showing: true,
          type: NOTIFICATION.TYPE.success,
          title: NOTIFICATION.TEXT.addRewardItemSuccess,
          content: [`Period reward: ${this.periodName}`, `Number of prizes: ${this.selectedRows.length}`],
          icon: NOTIFICATION.ICON.success,
          multiline: true,
          height: 0
        };
        this.$store.commit('notification/setNotification', data);
        this.$store.commit('rewardPeriod/addRewardItems', selectedRewardItems);
        this.displayDataPrizes = this.displayDataPrizes.filter(prize => {
          return !this.selectedRows.some(row => row.prizeId === prize.prizeId);
        });
      } else {
        const data = {
          showing: true,
          type: NOTIFICATION.TYPE.error,
          title: NOTIFICATION.TEXT.addRewardItemFailed,
          content: [],
          icon: NOTIFICATION.ICON.error,
          multiline: false,
          height: 0
        };
        this.$store.commit('notification/setNotification', data);
      }
    }
    this.$emit('onConfirmChanged', true);
    this.selectedRows = [];
  }

  buildEmptyRewardItem(prize: PrizeDTO): RewardItemDisplayDTO {
    return {
      id: prize.id,
      prizeId: prize.id,
      name: prize.name,
      image: prize.image,
      description: prize.description,
      type: prize.type,
      cost: 0,
      quantity: 0,
      redeemCount: 0,
      maxRedeemPerUser: null,
      estimatedPrice: 0
    };
  }

  normalizeMaxRedeemPerUser(value: number | string | null | undefined): number | null | -1 {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const normalizedValue = Number(value);
    if (!Number.isInteger(normalizedValue) || normalizedValue <= 0) {
      return -1;
    }
    return normalizedValue;
  }

  toRequestMaxRedeemPerUser(value: number | string | null | undefined): number | null {
    const normalizedValue = this.normalizeMaxRedeemPerUser(value);
    return normalizedValue === -1 ? null : normalizedValue;
  }

  onChangePrice(item: RewardItemDisplayDTO): void {
    const idx = this.dropMappingTable.VALUE_RANGE.findIndex(number => number * 1000 > item.estimatedPrice);
    const index = idx === 0 ? 0 : idx === -1 ? this.dropMappingTable.RATIO.length - 1 : idx - 1;
    item.cost = Math.ceil((item.estimatedPrice * this.dropMappingTable.RATIO[index]) / 1000);
  }

  async mounted(): Promise<void> {
    try {
      const payload = await fetchOperationSettings();
      const exchangeRatio = JSON.parse(payload.data.exchangeRatio) as ExchangeRatio;
      this.dropMappingTable = exchangeRatio;
    } catch (error) {
      console.error('Failed to fetch operation settings:', error);
    }
  }

  resetForm(): void {
    this.displayDataPrizes = this.displayDataPrizes.map(prize => this.buildEmptyRewardItem(prize));
    this.selectedRows = [];
    this.$refs.formRef.resetValidation();
  }

  onCancelChange(): void {
    this.$emit('onCancelChanged', true);
    this.resetForm();
  }

  getPrizeType(type: string): string {
    return displayPrizeType(type);
  }

  getImageSource(imgStr: string): string {
    return this.imageUrl + imgStr;
  }
}
